import { Head, router } from "@inertiajs/react"
import { AlertTriangle, GraduationCap } from "lucide-react"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { EmptyState } from "@/components/empty-state"
import { CreatorMasterclassCard } from "@/components/learn/creator-masterclass-card"
import { CreatorMasterclassCardSkeleton } from "@/components/learn/creator-masterclass-card-skeleton"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import AppLayout from "@/layouts/app-layout"
import { MOTION_BEATS } from "@/lib/motion"
import type { CreatorLearnIndexProps } from "@/types/learn"

/**
 * OKS-56 — Creator-side `/learn` index.
 *
 * Renders 5 states (ux-states skill):
 *   - loading  — 3 card-shaped Skeletons (layout-shift-safe; `aria-busy`)
 *   - empty    — `EmptyState` with `GraduationCap` icon + "No training yet"
 *   - error    — destructive Alert + "Try again" reload via partial
 *   - success  — vertical card list (mobile-first single column)
 *   - edge: all completed — same Success layout; every card carries
 *           the Completed pill (no special celebration in V1)
 *
 * # Brand isolation
 *
 * `masterclasses` + `completed_masterclass_ids` are server-scoped to
 * `Current.brand` + `Current.membership` respectively. No client-side
 * filter widens the surface. Switching brand context triggers a full
 * Inertia visit via `CrewSwitcher`.
 *
 * # Local ephemerals
 *
 * `loading` + `error` are local-only — driven by the retry-on-error
 * `router.reload` path (mirrors the `Creator/Missions` retry posture).
 * The `_forceLoading` / `_forceError` escape hatches are test-only
 * seams consumed by Vitest specs; they default to false and never
 * forward to the user.
 */
type Props = CreatorLearnIndexProps & {
  _forceLoading?: boolean
  _forceError?: boolean
}

export default function CreatorLearnIndex({
  brand_name,
  masterclasses,
  completed_masterclass_ids,
  is_empty,
  _forceLoading,
  _forceError,
}: Props) {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(_forceLoading ?? false)
  const [error, setError] = useState(_forceError ?? false)

  /**
   * Wrap the `number[]` from props in a `Set` once per render — O(1)
   * per-card lookup beats the per-card `.includes()` walk that would
   * land us in N×M territory on a 500-card library (TRD KD-10).
   */
  const completedSet = useMemo(
    () => new Set(completed_masterclass_ids),
    [completed_masterclass_ids],
  )

  function retry() {
    setLoading(true)
    setError(false)
    router.reload({
      only: ["masterclasses", "completed_masterclass_ids", "is_empty"],
      onError: () => setError(true),
      onFinish: () => setLoading(false),
    })
  }

  return (
    <AppLayout>
      <Head title={t("learn.head_title")} />
      <main className="mx-auto flex w-full max-w-3xl flex-col gap-6 px-4 py-6">
        {/*
          OKS-814 — apply the OKS-749 overflow clamp to this header caption,
          which interpolates `brand_name` OUTSIDE the shared `EmptyState`
          primitive and so never inherited that fix. A long UNBREAKABLE brand
          token would otherwise push horizontal page scroll at 320/375px.
          `wrap-anywhere` (`overflow-wrap: anywhere`) breaks the token so it
          wraps in-column; `min-w-0` lets the caption + header shrink below
          min-content in the flex flow. `w-full` is unnecessary here (unlike
          `empty-state.tsx`): `<header>`/`<main>` use the default
          `align-items: stretch`, so the `<p>` already has a definite width.

          Regression guard: `learn-index-header` is measured element-level by
          the OKS-808 helper. It is non-vacuous because `<header>` is a
          CROSS-AXIS flex item of the `flex-col` `<main>` — its width is
          `stretch`-pinned to the viewport-bounded main (the `min-width:auto`
          quirk applies to the vertical main axis, not width), so the unclamped
          token overflows this pinned box (scrollWidth > clientWidth) and
          `wrap-anywhere` collapses it back. The creator shell does NOT clip
          overflow-x, so this is a real user-visible page overflow, not a
          clip artifact.
        */}
        <header
          className="flex min-w-0 flex-col gap-1"
          data-testid="learn-index-header"
        >
          <h1 className="text-2xl font-bold">{t("learn.index.title")}</h1>
          <p className="text-muted-foreground min-w-0 text-sm wrap-anywhere">
            {t("learn.index.caption", { brand_name })}
          </p>
        </header>

        {error && (
          <Alert variant="destructive" data-testid="learn-index-error">
            <AlertTriangle aria-hidden="true" />
            <AlertTitle>{t("learn.state.error.title")}</AlertTitle>
            <AlertDescription>
              {t("learn.state.error.body")}
              <Button
                variant="outline"
                size="sm"
                onClick={retry}
                className="mt-2"
                data-testid="learn-index-retry"
              >
                {t("learn.state.error.retry")}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {!error && loading && (
          <div
            className="flex flex-col gap-3"
            data-testid="learn-index-loading"
            aria-busy="true"
            aria-live="polite"
          >
            <CreatorMasterclassCardSkeleton />
            <CreatorMasterclassCardSkeleton />
            <CreatorMasterclassCardSkeleton />
          </div>
        )}

        {!error && !loading && is_empty && (
          <EmptyState
            icon={GraduationCap}
            title={t("learn.state.empty.title")}
            // OKS-405 — brand-anchored body when the current brand is
            // resolvable; generic `_no_brand` fallback otherwise (never
            // another brand). `overlay-enter` plays once on mount.
            description={
              brand_name
                ? t("learn.state.empty.body", { brand_name })
                : t("learn.state.empty.body_no_brand")
            }
            className={MOTION_BEATS["overlay-enter"]}
          />
        )}

        {!error && !loading && !is_empty && (
          <ul
            className="flex flex-col gap-3"
            data-testid="learn-index-list"
            aria-label={t("learn.index.title")}
          >
            {masterclasses.map((masterclass) => (
              <li key={masterclass.id}>
                <CreatorMasterclassCard
                  masterclass={masterclass}
                  completed={completedSet.has(masterclass.id)}
                />
              </li>
            ))}
          </ul>
        )}
      </main>
    </AppLayout>
  )
}
